/**
 * Step Navigation Component
 *
 * Shows current step progress and allows navigation between steps.
 * Part of Quiz Studio wizard (Phase 3).
 */

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WizardStep } from "@/stores/useStudioStore";

interface StepNavigationProps {
  currentStep: WizardStep;
  completedSteps: Set<WizardStep>;
  onStepClick: (step: WizardStep) => void;
}

const STEP_LABELS: Record<WizardStep, { title: string; optional: boolean }> = {
  1: { title: "Dataset", optional: false },
  2: { title: "Prompts", optional: false },
  3: { title: "Filters", optional: true },
  4: { title: "Exclusions", optional: true },
  5: { title: "Scope", optional: true },
  6: { title: "Save & Play", optional: false },
};

export function StepNavigation({
  currentStep,
  completedSteps,
  onStepClick,
}: StepNavigationProps) {
  const steps: WizardStep[] = [1, 2, 3, 4, 5, 6];

  return (
    <nav className="border-b bg-card">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const { title, optional } = STEP_LABELS[step];
            const isCompleted = completedSteps.has(step);
            const isCurrent = step === currentStep;
            const isClickable = isCompleted || (step === 1); // Can always go back to step 1

            return (
              <div key={step} className="flex items-center flex-1">
                {/* Step Circle */}
                <button
                  onClick={() => isClickable && onStepClick(step)}
                  disabled={!isClickable}
                  className={cn(
                    "flex items-center gap-2 transition-all",
                    isClickable && "cursor-pointer hover:opacity-80",
                    !isClickable && "cursor-not-allowed opacity-50"
                  )}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all",
                      isCurrent &&
                        "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2",
                      isCompleted &&
                        !isCurrent &&
                        "bg-green-500 text-white",
                      !isCompleted &&
                        !isCurrent &&
                        "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted && !isCurrent ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step
                    )}
                  </div>
                  <div className="text-left">
                    <div
                      className={cn(
                        "text-sm font-medium",
                        isCurrent && "text-primary",
                        !isCurrent && "text-muted-foreground"
                      )}
                    >
                      {title}
                    </div>
                    {optional && (
                      <div className="text-xs text-muted-foreground">
                        Optional
                      </div>
                    )}
                  </div>
                </button>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "flex-1 h-0.5 mx-2 transition-all",
                      isCompleted ? "bg-green-500" : "bg-muted"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
